// Apolo IDE — Evaluator side-panel (cross-session memory / V3).
//
// Durable memory lives in `.apolorules` → LEARNED block → git. Distinct from:
//   • Rules tab — manual project constitution
//   • Learned tab — ephemeral corrections for THIS session only
//   • Learning tab — vault skills / auto-skills pipeline

import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, Check, CheckCircle2, Loader2, Play, RotateCcw, Sparkles, Radio, Brain, AlertTriangle, EyeOff, Eye } from 'lucide-react';
import { useAgentStore } from '../../../store/useAgentStore.js';
import { useEvaluator } from '../../../hooks/useEvaluator.js';
import { fetchOutcomesSummary } from '../../../hooks/useOutcomes.js';
import { syncExternalSignalFromGit } from '../../../lib/finalizeExternalOutcome.js';
import { formatSelfImproveError } from '../../../lib/formatSelfImproveError.js';
import { EVALUATOR_CLOUD_MODEL, EVALUATOR_SCHEDULE_NAME, ensureEvaluatorScheduleTimezone } from '../../../lib/evaluatorLocal.js';

const GATE_MIN_N = 5;
const GATE_MIN_EXT = 0.5;

const ALERT_STYLES = {
  success: 'border-apolo-success/30 bg-apolo-successBg text-apolo-success',
  error: 'border-apolo-error/30 bg-apolo-errorBg text-apolo-error',
  info: 'border-apolo-borderSubtle bg-apolo-surface2 text-apolo-textSecondary',
  warning: 'border-apolo-warning/30 bg-apolo-warning/10 text-apolo-warning',
};

function AlertBanner({ kind, text }) {
  if (!text) return null;
  const Icon = kind === 'success' ? CheckCircle2 : kind === 'warning' ? AlertTriangle : AlertCircle;
  return (
    <div
      role="alert"
      className={`flex items-start gap-2 rounded-md border p-2 text-xs ${ALERT_STYLES[kind] || ALERT_STYLES.info}`}
    >
      <Icon size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
      <span>{text}</span>
    </div>
  );
}

function summarizeLedger(stats) {
  if (!Array.isArray(stats) || stats.length === 0) {
    return { totalRuns: 0, bestExt: 0, gateReady: false };
  }
  let totalRuns = 0;
  let bestExt = 0;
  let gateReady = false;
  for (const s of stats) {
    const n = Number(s.n) || 0;
    const ext = Number(s.ext_signal_coverage) || 0;
    totalRuns += n;
    if (ext > bestExt) bestExt = ext;
    if (n >= GATE_MIN_N && ext >= GATE_MIN_EXT) gateReady = true;
  }
  return { totalRuns, bestExt, gateReady };
}

export default function LearnedRules({ sessionId }) {
  const session = useAgentStore((s) => s.code.sessions.find((x) => x.id === sessionId));
  const root = session?.worktree_path || session?.directory || '';
  const {
    proposals,
    running,
    error,
    run,
    loadPending,
    dismissPending,
    list,
    apply,
    revert,
    setProposals,
  } = useEvaluator(root, sessionId);
  const [learned, setLearned] = useState([]);
  const [busy, setBusy] = useState(null);
  const [notice, setNotice] = useState(null); // null | { kind: 'success'|'error'|'info'|'warning', text: string }
  const [ledger, setLedger] = useState({ totalRuns: 0, bestExt: 0, gateReady: false });

  const reload = useCallback(async () => {
    setLearned(await list());
  }, [list]);

  const refreshLedger = useCallback(async () => {
    const summary = await fetchOutcomesSummary({}, { bypassCache: true });
    setLedger(summarizeLedger(summary));
  }, []);

  useEffect(() => {
    reload();
    refreshLedger();
    ensureEvaluatorScheduleTimezone();
  }, [reload, refreshLedger]);

  useEffect(() => {
    const onProposals = (ev) => {
      const { proposals: next = [] } = ev.detail || {};
      if (next.length) setProposals(next);
    };
    window.addEventListener('apolo:evaluator:proposals', onProposals);
    return () => window.removeEventListener('apolo:evaluator:proposals', onProposals);
  }, [setProposals]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const pending = await loadPending();
      if (cancelled || !pending?.count) return;
      window.dispatchEvent(new CustomEvent('apolo:toast', {
        detail: {
          kind: 'info',
          message: `Avaliador: ${pending.count} proposta(s) carregada(s) da fila semanal.`,
        },
      }));
      await dismissPending();
    })();
    return () => { cancelled = true; };
  }, [sessionId, loadPending, dismissPending]);

  const onSyncExternal = useCallback(async () => {
    setNotice(null);
    const res = await syncExternalSignalFromGit(sessionId, root);
    if (!res.ok) setNotice({ kind: 'error', text: res.error || 'Falha ao sincronizar sinal externo' });
    else {
      setNotice({ kind: 'success', text: 'Sinal externo aplicado ao último outcome desta sessão.' });
      await refreshLedger();
    }
  }, [sessionId, root, refreshLedger]);

  const onRun = useCallback(async () => {
    setNotice(null);
    const summary = await fetchOutcomesSummary({}, { bypassCache: true });
    const freshLedger = summarizeLedger(summary);
    setLedger(freshLedger);

    const res = await run('all');
    await refreshLedger();
    if (res.error) return;
    if (res.proposals.length > 0) {
      if (res.fallback_used) {
        setNotice({
          kind: 'warning',
          text: `Gemma não retornou JSON válido — ${res.proposals.length} regra(s) heurística(s) gerada(s) a partir dos ${res.gated_count} stat(s) com gate. Revise antes de aplicar.`,
        });
      } else {
        setNotice({
          kind: 'success',
          text: `Avaliador concluiu — ${res.proposals.length} proposta(s) pronta(s) para aplicar.`,
        });
      }
      return;
    }

    if (res.model_error) {
      setNotice({ kind: 'error', text: formatSelfImproveError(res.model_error) });
    } else if (res.considered === 0) {
      setNotice({ kind: 'info', text: 'Ainda não há execuções no ledger. Rode o agente no chat — cada run grava um outcome ao terminar.' });
    } else if ((res.gated_count ?? 0) === 0) {
      setNotice({
        kind: 'info',
        text: `Avaliador rodou (${res.considered} stat(s)), mas o gate de evidência externa não passou. `
          + `Precisa ≥${GATE_MIN_N} runs e ≥${GATE_MIN_EXT * 100}% com sinal git/merge. Use "Sincronizar sinal git".`,
      });
    } else if ((res.parsed_count ?? 0) === 0) {
      setNotice({
        kind: 'info',
        text: `Avaliador rodou (${res.gated_count} stat(s) com gate OK), mas não propôs regras válidas. `
          + `Ele julga com ${EVALUATOR_CLOUD_MODEL} (cloud); sem chave Google configurada, cai pro modelo local (Ollama).`,
      });
    } else {
      setNotice({ kind: 'info', text: 'Avaliador rodou — nenhuma regra nova passou no gate desta vez.' });
    }
  }, [run, refreshLedger]);

  const onApply = useCallback(async (proposal) => {
    setBusy(proposal.rule_id);
    setNotice(null);
    const res = await apply([...learned, proposal], proposal.rule_id);
    if (!res.ok) setNotice({ kind: 'error', text: res.error || 'Falha ao aplicar' });
    else {
      setProposals((prev) => prev.filter((p) => p.rule_id !== proposal.rule_id));
      if (res.syncWarning) {
        setNotice({ kind: 'warning', text: res.syncWarning });
      } else {
        setNotice({ kind: 'success', text: 'Regra aplicada no `.apolorules`, commitada e sincronizada com esta sessão.' });
      }
      await reload();
    }
    setBusy(null);
  }, [apply, learned, reload, setProposals]);

  const onRevert = useCallback(async (ruleId) => {
    setBusy(ruleId);
    setNotice(null);
    const res = await revert(ruleId);
    if (!res.ok) setNotice({ kind: 'error', text: res.error || 'Falha ao reverter' });
    else {
      setNotice({ kind: 'success', text: 'Regra revertida via git revert.' });
      await reload();
    }
    setBusy(null);
  }, [revert, reload]);

  // E4 — disable/enable toggle: re-apply the full learned list with the target
  // rule's disabled field flipped. Uses the same apply path so changes are
  // committed atomically and synced to the session (auditável via git).
  const onToggleDisabled = useCallback(async (targetRule) => {
    const ruleId = targetRule.rule_id;
    setBusy(ruleId);
    setNotice(null);
    const updatedList = learned.map((r) =>
      r.rule_id === ruleId ? { ...r, disabled: !r.disabled } : r,
    );
    const res = await apply(updatedList, ruleId);
    if (!res.ok) setNotice({ kind: 'error', text: res.error || 'Falha ao alterar estado da regra' });
    else {
      const action = targetRule.disabled ? 'reabilitada' : 'desabilitada';
      setNotice({ kind: 'success', text: `Regra ${action} no \`.apolorules\` e commitada.` });
      await reload();
    }
    setBusy(null);
  }, [apply, learned, reload]);

  const ledgerHint = useMemo(() => {
    if (ledger.totalRuns === 0) {
      return 'Ledger vazio — memória entre sessões começa depois que o agente roda e grava outcomes.';
    }
    const extPct = Math.round(ledger.bestExt * 100);
    if (ledger.gateReady) {
      return `${ledger.totalRuns} run(s) no ledger · até ${extPct}% com sinal externo · gate OK para propor regras.`;
    }
    return `${ledger.totalRuns} run(s) no ledger · até ${extPct}% com sinal externo · gate: ≥${GATE_MIN_N} runs e ≥${GATE_MIN_EXT * 100}% externo.`;
  }, [ledger]);

  if (!sessionId) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-center text-xs text-apolo-textTertiary">
        Abra uma sessão para ver as regras aprendidas.
      </div>
    );
  }

  const errorText = error ? formatSelfImproveError(error) : null;

  return (
    <div className="flex h-full flex-col gap-3 p-3">
      <div className="flex items-start gap-2 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 p-2.5 text-xs text-apolo-textSecondary">
        <Brain size={14} className="mt-0.5 shrink-0 text-apolo-accent" aria-hidden="true" />
        <div className="leading-relaxed">
          <strong className="text-apolo-textPrimary">Memória entre sessões</strong>
          {' — regras duráveis no '}
          <code className="text-[10px]">.apolorules</code>
          {' (bloco LEARNED) + git. Persistem entre sessões e entram no prompt do agente. '
            + 'Não confundir com a tab '}
          <strong>Session</strong>
          {' (só esta sessão) nem '}
          <strong>Learning</strong>
          {' (vault/skills).'}
        </div>
      </div>

      <p className="text-[11px] leading-relaxed text-apolo-textTertiary">{ledgerHint}</p>

      <div className="text-xs text-apolo-textTertiary">
        O avaliador lê só agregados (nunca código cru), julga com{' '}
        <code className="text-[10px]">{EVALUATOR_CLOUD_MODEL}</code>
        {' (cloud, qualidade) e cai pro modelo local (Ollama) se não houver chave Google. '}
        Propõe regras com evidência externa (merge/revert git).
        Agendamento: ative &quot;{EVALUATOR_SCHEDULE_NAME}&quot; na aba Schedules (padrão 4h).
        Cada regra aplicada vira um commit reversível.
      </div>

      <button
        type="button"
        onClick={onRun}
        disabled={running}
        className="inline-flex w-fit items-center gap-1.5 rounded-md bg-apolo-accent px-3 py-1.5 text-xs font-medium text-apolo-bg transition-colors hover:bg-apolo-accent/90 disabled:opacity-50"
      >
        {running ? <Loader2 size={12} className="animate-spin" aria-hidden="true" /> : <Play size={12} aria-hidden="true" />}
        Rodar avaliador
      </button>

      {root && (
        <button
          type="button"
          onClick={onSyncExternal}
          className="inline-flex w-fit items-center gap-1.5 rounded-md border border-apolo-borderSubtle px-3 py-1.5 text-xs text-apolo-textSecondary transition hover:border-apolo-borderStrong"
        >
          <Radio size={12} aria-hidden="true" />
          Sincronizar sinal git (E5)
        </button>
      )}

      {errorText && <AlertBanner kind="error" text={errorText} />}
      {notice?.text && <AlertBanner kind={notice.kind} text={notice.text} />}

      {proposals.length > 0 && (
        <section className="flex flex-col gap-2">
          <h3 className="text-[11px] font-medium uppercase tracking-wide text-apolo-textTertiary">Propostas</h3>
          {proposals.map((p) => (
            <div key={p.rule_id} className="flex items-start gap-2 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 p-2 text-xs">
              <span className="flex-1 leading-relaxed text-apolo-textPrimary">{p.rule}</span>
              <span className="shrink-0 text-[10px] text-apolo-textTertiary" title={`${p.n} execuções, ${p.ext_pct}% com sinal externo`}>
                {p.ext_pct}%
              </span>
              <button
                type="button"
                onClick={() => onApply(p)}
                disabled={busy === p.rule_id}
                className="inline-flex h-6 shrink-0 items-center gap-1 rounded border border-apolo-borderSubtle px-1.5 text-[11px] text-apolo-textSecondary transition hover:border-apolo-borderStrong hover:text-apolo-textPrimary disabled:opacity-50"
              >
                <Check size={11} aria-hidden="true" /> Aplicar
              </button>
            </div>
          ))}
        </section>
      )}

      <section className="flex flex-col gap-2">
        <h3 className="text-[11px] font-medium uppercase tracking-wide text-apolo-textTertiary">Regras aprendidas (persistem entre sessões)</h3>
        {learned.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 p-6 text-center text-xs text-apolo-textTertiary">
            <Sparkles size={18} aria-hidden="true" />
            <span>Nenhuma regra aprendida ainda.</span>
            <span className="max-w-[240px] leading-relaxed">
              1) Rode o agente no chat · 2) Sincronize sinal git · 3) Rodar avaliador · 4) Aplicar proposta
            </span>
          </div>
        ) : (
          learned.map((r) => (
            <div
              key={r.rule_id || r.rule}
              className={`flex flex-col gap-1 rounded-md border bg-apolo-surface2 p-2 text-xs${r.disabled ? ' border-apolo-borderSubtle/50 opacity-60' : ' border-apolo-borderSubtle'}`}
            >
              {/* Rule text — dimmed when disabled */}
              <span className={`leading-relaxed${r.disabled ? ' text-apolo-textTertiary line-through' : ' text-apolo-textPrimary'}`}>
                {r.rule}
              </span>

              {/* E4 — Provenance row: evidence count, ext_pct, generated_at */}
              <div className="flex items-center justify-between gap-2">
                <span
                  className="text-[10px] text-apolo-textTertiary"
                  title={`${r.n} execuções · ${r.ext_pct}% com sinal externo · gerada em ${r.generated_at}`}
                >
                  {r.n} runs · {r.ext_pct}% ext · {r.generated_at ? new Date(r.generated_at).toLocaleDateString() : '—'}
                  {r.disabled && <span className="ml-1 font-medium text-apolo-warning">[desabilitada]</span>}
                </span>

                <div className="flex shrink-0 items-center gap-1">
                  {/* E4 — Disable / Re-enable toggle */}
                  <button
                    type="button"
                    onClick={() => onToggleDisabled(r)}
                    disabled={busy === r.rule_id || !r.rule_id}
                    aria-label={r.disabled ? 'Reabilitar regra' : 'Desabilitar regra'}
                    title={r.disabled ? 'Reabilitar regra (remove # DISABLED do .apolorules)' : 'Desabilitar regra (mantém no arquivo mas não injeta no prompt)'}
                    className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded border border-apolo-borderSubtle text-apolo-textTertiary transition hover:border-apolo-borderStrong hover:text-apolo-textPrimary disabled:opacity-50"
                  >
                    {r.disabled ? <Eye size={12} aria-hidden="true" /> : <EyeOff size={12} aria-hidden="true" />}
                  </button>

                  {/* Revert button — permanent git revert */}
                  <button
                    type="button"
                    onClick={() => onRevert(r.rule_id)}
                    disabled={busy === r.rule_id || !r.rule_id}
                    aria-label="Reverter regra"
                    title="Reverter regra permanentemente (git revert do commit)"
                    className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded border border-apolo-borderSubtle text-apolo-textTertiary transition hover:border-apolo-borderStrong hover:text-apolo-textPrimary disabled:opacity-50"
                  >
                    <RotateCcw size={12} aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </section>
    </div>
  );
}
